import moment from "moment";
import { range } from "lodash";
import { getYear } from "date-fns";

import {
  DatePicker,
  Label,
  Select,
  customStyles,
} from "../../fall-analysis/form/sharedImports";
import ActionBtnU from "../../../Users/components/ActionBtnU";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

import calender_icon from "../../../../../assets/images/calendar_icon.svg";
import { CalenderIcon } from "../../../../../../src/components/DateComponent/DateComponent.styles";

const years = range(1990, getYear(new Date()) + 1, 1);
const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
type DefaultSelectProps = {
  options: any[];
  onChange: (option: any) => void;
  selected: any;
  isMulti: boolean;
  name: string;
  placeholder: string;
};

type IncidentTypeProps = {
  name: string;
  placeholder: string;
  isMulti: boolean;
  options: any[];
  onChange: (option: any) => void;
  selected?: any;
};

type DateRangeProps = {
  startDate: string | null;
  endDate: string | null;
  setFieldValue: (field: string, value: string | null) => void;
  limitOneYear?: boolean;
};

const FacilitySelect = ({
  options,
  onChange,
  selected,
  isMulti,
  name,
  placeholder,
}: DefaultSelectProps) => {
  return (
    <div className="mb-7">
      <Label>Select Facility</Label>
      <Select
        name={name}
        placeholder={placeholder}
        options={options}
        className="basic-multi-select"
        classNamePrefix="select"
        styles={customStyles}
        isMulti={isMulti}
        onChange={onChange}
        value={selected}
      />
    </div>
  );
};

const IncidentTypeSelect = ({
  options,
  onChange,
  name,
  placeholder,
  isMulti,
  selected,
}: IncidentTypeProps) => (
  <div className="mb-7">
    <Label>Incident Type</Label>
    <Select
      name={name}
      placeholder={placeholder}
      options={options}
      className="basic-multi-select"
      classNamePrefix="select"
      styles={customStyles}
      isMulti={isMulti}
      onChange={onChange}
      value={selected}
    />
  </div>
);

const SkinIssueTypeSelect = ({
  options,
  onChange,
  name,
  placeholder,
  isMulti,
  selected,
}: DefaultSelectProps) => (
  <div className="mb-7">
    <Label>Skin Issue Type</Label>
    <Select
      name={name}
      placeholder={placeholder}
      options={options}
      className="basic-multi-select"
      classNamePrefix="select"
      styles={customStyles}
      isMulti={isMulti}
      onChange={onChange}
      value={selected}
    />
  </div>
);

const DateRangePicker = ({
  startDate,
  endDate,
  setFieldValue,
  limitOneYear,
}: DateRangeProps) => (
  <div className="z-50 mb-4 flex flex-wrap">
    <div className="mb-2 flex-1">
      <Label>Start Date</Label>
      <CustomDatePicker
        setFieldValue={setFieldValue}
        endDate={endDate}
        startDate={startDate}
        isStartDate={true}
        // limitOneYear={limitOneYear}
      />
    </div>
    <div className="flex-1 sm:ml-0 lg:ml-4">
      <Label>End Date</Label>
      <CustomDatePicker
        setFieldValue={setFieldValue}
        endDate={endDate}
        startDate={startDate}
        isStartDate={false}
        // limitOneYear={limitOneYear}
      />
    </div>
  </div>
);

export { FacilitySelect, SkinIssueTypeSelect, IncidentTypeSelect, DateRangePicker };

export interface CustomDatePickerProps {
  setFieldValue?: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
  endDate: string | null;
  startDate: string | null;
  isStartDate: boolean;
  handleSetStartDates?: (date: string | null) => void;
  handleSetEndDates?: (date: string | null) => void;
  noBorder?: boolean;
  limitOneYear?: boolean;
}

export function CustomDatePicker({
  setFieldValue,
  endDate,
  startDate,
  isStartDate,
  handleSetStartDates,
  handleSetEndDates,
  noBorder,
  limitOneYear = false,
}: Readonly<CustomDatePickerProps>) {
  const formatDate = (date: Date | null): string | null =>
    date ? moment(date).format("MM-DD-YYYY") : null;

  const handleStartDateChange = (formattedDate: string | null) => {
    setFieldValue?.("startDate", formattedDate);

    if (!endDate && formattedDate) {
      const newEndDate = limitOneYear
        ? moment(formattedDate, "MM-DD-YYYY")
            .add(1, "year")
            .format("MM-DD-YYYY")
        : moment().format("MM-DD-YYYY");
      setFieldValue?.("endDate", newEndDate);
      handleSetEndDates?.(newEndDate);
    } else if (endDate && formattedDate && limitOneYear) {
      const start = moment(formattedDate, "MM-DD-YYYY");
      const end = moment(endDate, "MM-DD-YYYY");
      if (end.diff(start, "years", true) > 1) {
        const newEndDate = start.add(1, "year").format("MM-DD-YYYY");
        setFieldValue?.("endDate", newEndDate);
        handleSetEndDates?.(newEndDate);
      }
    }
  };

  const handleEndDateChange = (formattedDate: string | null) => {
    setFieldValue?.("endDate", formattedDate);

    if (startDate && formattedDate && limitOneYear) {
      const start = moment(startDate, "MM-DD-YYYY");
      const end = moment(formattedDate, "MM-DD-YYYY");
      if (end.diff(start, "years", true) > 1) {
        const newStartDate = end.subtract(1, "year").format("MM-DD-YYYY");
        setFieldValue?.("startDate", newStartDate);
        handleSetStartDates?.(newStartDate);
      }
    }
  };

  const handleDateChange = (date: Date | null) => {
    const formattedDate = formatDate(date);

    if (isStartDate) {
      handleStartDateChange(formattedDate);
      handleSetStartDates?.(formattedDate);
    } else {
      handleEndDateChange(formattedDate);
      handleSetEndDates?.(formattedDate);
    }
  };

  const getSelectedDate = () => {
    const dateString = isStartDate ? startDate : endDate;
    return dateString ? moment(dateString, "MM-DD-YYYY").toDate() : null;
  };

  const setMaxDate = () =>
    isStartDate
      ? endDate
        ? moment(endDate, "MM-DD-YYYY").toDate()
        : undefined
      : new Date();

  const setMinDate = () =>
    !isStartDate
      ? startDate
        ? moment(startDate, "MM-DD-YYYY").toDate()
        : undefined
      : undefined;

  return (
    <DatePicker
      placeholderText="Select Date"
      data-testid="date-picker"
      renderCustomHeader={({
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
      }) => (
        <div className="z-50 flex flex-col items-center space-y-2 px-2 py-2">
          <ActionBtnU
            borderStyle="1px solid rgb(117, 84, 137)"
            color="rgb(115, 64, 167)"
            height="30px"
            onClick={() => {
              const today = new Date();
              changeYear(today.getFullYear());
              changeMonth(today.getMonth());
              handleDateChange(today);
            }}
          >
            Today
          </ActionBtnU>
          <div className="flex w-full items-center justify-between">
            <button
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              className="mr-2 flex cursor-pointer items-center p-1"
              type="button"
            >
              <FaChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex space-x-2">
              {/* years */}

              <select
                data-testid="year-select"
                value={date.getFullYear()}
                onChange={({ target: { value } }) => changeYear(Number(value))}
                className="bg-white h-9 appearance-none overflow-hidden rounded-md border border-gray-300 pl-2 pr-8 text-gray-700 hover:border-gray-400 focus:outline-none"
              >
                {years.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {/* months */}
              <select
                data-testid="month-select"
                value={months[date.getMonth()]}
                onChange={({ target: { value } }) =>
                  changeMonth(months.indexOf(value))
                }
                className="bg-white h-9 appearance-none overflow-scroll rounded-md border border-gray-300 pl-2 pr-8 text-gray-700 hover:border-gray-400 focus:outline-none"
              >
                {months.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              className="ml-2 p-1"
              type="button"
            >
              <FaChevronRight className="flex h-4 w-4 cursor-pointer items-center" />
            </button>
          </div>
        </div>
      )}
      wrapperClassName="w-full"
      dateFormat="MM/dd/yyyy"
      maxDate={setMaxDate()}
      minDate={setMinDate()}
      onChange={handleDateChange}
      selected={getSelectedDate()}
      showIcon
      icon={
        <CalenderIcon
          src={calender_icon}
          alt="calender icon"
          className="mr-2"
        />
      }
      toggleCalendarOnIconClick
      customInput={
        <input
          id="date-input"
          data-testid="date-input"
          className="w-full border-none pl-9"
        />
      }
      calendarClassName="rounded-lg border border-gray-200 shadow-lg"
      renderDayContents={(day) => (
        <div className="mx-auto flex h-8 items-center justify-center rounded-full text-center">
          {day}
        </div>
      )}
    />
  );
}
